import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/api";
import { formatDeadline } from "@/lib/utils";
import { AlertTriangle, CheckCircle2, Clock, ZapOff, CalendarClock, TrendingUp, ChevronRight, ListFilter, X } from "lucide-react";

const PRIORITY_COLORS = {
    critical: { bg: "bg-red-100", text: "text-red-700", label: "Critic" },
    high: { bg: "bg-orange-100", text: "text-orange-700", label: "Ridicat" },
    medium: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Mediu" },
    low: { bg: "bg-gray-100", text: "text-gray-600", label: "Scăzut" },
};

function StatCard({ label, value, icon, color, testId, active, onClick }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`text-left glass-panel rounded-xl p-5 flex items-start gap-4 transition-all ${active ? "ring-2 ring-[#002FA7]/40" : "hover:-translate-y-0.5 hover:shadow-md"}`}
        >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                {icon}
            </div>
            <div>
                <p className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</p>
                <p className="text-xs text-[#9CA3AF] mt-0.5 font-medium uppercase tracking-wide">{label}</p>
            </div>
        </button>
    );
}

function TaskItem({ task, onClick }) {
    const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !task.completed_at;
    const priority = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
    return (
        <div
            data-testid="task-item"
            onClick={onClick}
            className="flex items-center gap-3 py-2.5 px-3 rounded-md hover:bg-[#F8F9FA] cursor-pointer transition-colors group"
        >
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${priority.bg.replace("bg-", "bg-")}`}
                style={{ backgroundColor: task.priority === "critical" ? "#EF4444" : task.priority === "high" ? "#F97316" : task.priority === "medium" ? "#F59E0B" : "#6B7280" }}
            />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#111827] truncate">{task.title}</p>
                <p className="text-xs text-[#9CA3AF] truncate">{task.board_name} · {task.column_name}</p>
            </div>
            {task.due_date && (
                <span className={`text-xs flex-shrink-0 font-medium ${isOverdue ? "text-red-600" : "text-[#9CA3AF]"}`}>
                    {formatDeadline(task.due_date)}
                </span>
            )}
            <ChevronRight size={14} className="text-gray-300 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
    );
}

const ACTION_LABELS = {
    "task.created": "a creat task",
    "task.updated": "a actualizat task",
    "task.moved": "a mutat task",
    "task.archived": "a arhivat task",
    "board.created": "a creat board",
    "team.created": "a creat echipă",
    "workspace.created": "a creat workspace",
    "comment.added": "a comentat pe task",
    "attachment.added": "a atașat fișier la task",
};

export default function DashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | active | overdue | done | blocked | today

    useEffect(() => {
        API.get("/dashboard")
            .then(r => setData(r.data))
            .finally(() => setLoading(false));
    }, []);

    const filteredTasks = useMemo(() => {
        const tasks = data?.my_tasks || [];
        switch (filter) {
            case "active": return tasks.filter(t => t.is_active);
            case "overdue": return tasks.filter(t => t.is_overdue);
            case "done": return tasks.filter(t => t.is_done_recent);
            case "blocked": return tasks.filter(t => t.is_blocked);
            case "today": return tasks.filter(t => t.is_due_today);
            default: return tasks.filter(t => t.is_active); // "all" pe Taskurile mele = active by default
        }
    }, [data, filter]);

    const FILTER_LABELS = {
        all: "Toate (active)",
        active: "Active",
        overdue: "Întârziate",
        done: "Finalizate săptămâna",
        blocked: "Blocate / Stand By",
        today: "Scadente azi",
    };

    const toggleFilter = (key) => setFilter(prev => prev === key ? "all" : key);

    if (loading) return (
        <div className="p-6 space-y-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 glass-panel rounded-xl animate-pulse" />
            ))}
        </div>
    );

    const stats = data?.stats || {};

    return (
        <div className="p-6 max-w-7xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Bun venit, {user?.name?.split(" ")[0]}
                </h1>
                <p className="text-sm text-[#9CA3AF] mt-1">
                    {new Date().toLocaleDateString("ro-RO", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="dashboard-stats">
                <StatCard testId="stat-active" label="Taskuri active" value={stats.active_tasks ?? 0} icon={<TrendingUp size={18} className="text-blue-600" />} color="bg-blue-50" active={filter === "active"} onClick={() => toggleFilter("active")} />
                <StatCard testId="stat-overdue" label="Întârziate" value={stats.overdue_tasks ?? 0} icon={<AlertTriangle size={18} className="text-red-600" />} color="bg-red-50" active={filter === "overdue"} onClick={() => toggleFilter("overdue")} />
                <StatCard testId="stat-done" label="Finalizate săptămâna" value={stats.done_this_week ?? 0} icon={<CheckCircle2 size={18} className="text-green-600" />} color="bg-green-50" active={filter === "done"} onClick={() => toggleFilter("done")} />
                <StatCard testId="stat-blocked" label="Blocate" value={stats.blocked_tasks ?? 0} icon={<ZapOff size={18} className="text-orange-600" />} color="bg-orange-50" active={filter === "blocked"} onClick={() => toggleFilter("blocked")} />
                <StatCard testId="stat-today" label="Scadente azi" value={stats.due_today ?? 0} icon={<CalendarClock size={18} className="text-purple-600" />} color="bg-purple-50" active={filter === "today"} onClick={() => toggleFilter("today")} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* My Tasks */}
                <div className="lg:col-span-2 glass-panel rounded-2xl" data-testid="my-tasks-section">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E4E7] gap-2 flex-wrap">
                        <h2 className="font-semibold text-[#111827] text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Taskurile mele</h2>
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <ListFilter size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
                                <select
                                    data-testid="my-tasks-filter"
                                    value={filter}
                                    onChange={e => setFilter(e.target.value)}
                                    className="pl-7 pr-7 py-1 border border-[#E4E4E7] rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-[#002FA7] bg-white appearance-none cursor-pointer"
                                >
                                    {Object.entries(FILTER_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>
                            {filter !== "all" && (
                                <button
                                    data-testid="my-tasks-filter-clear"
                                    onClick={() => setFilter("all")}
                                    className="text-[10px] text-[#6B7280] hover:text-[#111827] inline-flex items-center gap-0.5">
                                    <X size={10} /> Resetează
                                </button>
                            )}
                            <span className="text-xs text-[#9CA3AF] font-medium">{filteredTasks.length}</span>
                        </div>
                    </div>
                    <div className="divide-y divide-[#F1F3F5] px-2 py-1">
                        {filteredTasks.length === 0 ? (
                            <div className="py-10 text-center text-sm text-[#9CA3AF]" data-testid="my-tasks-empty">
                                {filter === "all" ? "Niciun task atribuit" : `Niciun task: ${FILTER_LABELS[filter]?.toLowerCase()}`}
                            </div>
                        ) : (
                            filteredTasks.map(task => (
                                <TaskItem key={task.id} task={task} onClick={() => navigate(`/boards/${task.board_id}?task=${task.id}`)} />
                            ))
                        )}
                    </div>
                </div>

                {/* Right column */}
                <div className="space-y-6">
                    {/* Due soon */}
                    <div className="glass-panel rounded-2xl" data-testid="due-soon-section">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-semibold text-[#111827] text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Scadente curând</h2>
                        </div>
                        <div className="px-2 py-1">
                            {(data?.due_soon || []).length === 0 ? (
                                <div className="py-6 text-center text-sm text-[#9CA3AF]">Nicio scadență în 7 zile</div>
                            ) : (
                                (data?.due_soon || []).slice(0, 5).map(task => (
                                    <div key={task.id} className="flex items-center gap-2 py-2 px-2 hover:bg-[#F8F9FA] rounded cursor-pointer" onClick={() => navigate(`/boards/${task.board_id}`)}>
                                        <Clock size={14} className="text-[#9CA3AF] flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-[#111827] truncate">{task.title}</p>
                                            <p className="text-xs text-[#9CA3AF]">{formatDeadline(task.due_date)}</p>
                                        </div>
                                        {task.assignee && (
                                            <div className="w-6 h-6 bg-[#002FA7] rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-white text-xs">{task.assignee.name?.[0]}</span>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Activity */}
                    <div className="glass-panel rounded-2xl" data-testid="activity-section">
                        <div className="px-5 py-4 border-b border-[#E4E4E7]">
                            <h2 className="font-semibold text-[#111827] text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Activitate recentă</h2>
                        </div>
                        <div className="px-4 py-2 space-y-3">
                            {(data?.recent_activity || []).length === 0 ? (
                                <div className="py-6 text-center text-sm text-[#9CA3AF]">Nicio activitate recentă</div>
                            ) : (
                                (data?.recent_activity || []).slice(0, 8).map(act => (
                                    <div key={act.id} className="flex items-start gap-2.5">
                                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <span className="text-xs text-gray-500 font-semibold">{act.actor?.name?.[0] || "?"}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-[#111827]">
                                                <span className="font-semibold">{act.actor?.name || "Utilizator"}</span>
                                                {" "}{ACTION_LABELS[act.action] || act.action}
                                                {act.metadata?.title ? ` "${act.metadata.title}"` : ""}
                                            </p>
                                            <p className="text-xs text-[#9CA3AF] mt-0.5">
                                                {new Date(act.created_at).toLocaleString("ro-RO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
